import { useRef, useState } from "react";

/**
 * Custom hook for managing webcam functionality and camera controls
 * 
 * This hook provides comprehensive state management for camera operations including
 * camera switching, flash control, aspect ratio settings, error handling, and
 * timer functionality for delayed photo capture.
 * 
 * Key Features:
 * - Front/back camera switching with proper state management
 * - Flash/torch control for supported devices
 * - Multiple aspect ratio support (1:1, 4:3, 9:16, 16:9)
 * - Comprehensive error handling for camera permissions and devices
 * - Timer functionality for delayed photo capture (0s, 3s, 10s)
 * - Countdown display management
 * - DOM element references for webcam access
 * 
 * Camera Modes:
 * - Facing Mode: 'user' (front camera) or 'environment' (back camera)
 * - Flash Mode: 'off', 'on', 'auto' (if supported)
 * - Aspect Ratios: '1:1' (square), '4:3' (standard), '9:16' (portrait), '16:9' (landscape)
 * 
 * Common Usage Patterns:
 * - Camera component initialization and control
 * - Camera permission handling and error display
 * - Photo capture workflow with timer options
 * - Camera settings UI management
 * - Device compatibility handling
 * 
 * @returns {Object} - Hook state and utility functions
 * @returns {React.RefObject} returns.webcamRef - Reference to webcam DOM element for screenshot capture
 * @returns {Function} returns.flipCamera - Function to switch between front/back camera
 * @returns {string} returns.facingMode - Current camera direction ('user' for front, 'environment' for back)
 * @returns {string} returns.flashMode - Current flash setting ('off', 'on', 'auto')
 * @returns {Function} returns.setFlashMode - Function to change flash mode
 * @returns {string} returns.ratio - Current aspect ratio setting ('1:1', '4:3', '9:16', '16:9')
 * @returns {Function} returns.setRatio - Function to change aspect ratio
 * @returns {string|null} returns.cameraError - Camera error message or null if no error
 * @returns {Function} returns.handleCameraError - Function to handle camera errors with user-friendly messages
 * @returns {number} returns.timer - Timer duration for delayed capture (0=immediate, 3=3sec, 10=10sec)
 * @returns {Function} returns.setTimer - Function to set timer duration
 * @returns {number|null} returns.countdown - Current countdown value or null when not counting
 * @returns {Function} returns.setCountdown - Function to update countdown display
 */
const useWebcamHook = () => {
  // Reference to the webcam DOM element
  // Used for accessing webcam methods like getScreenshot() and camera controls
  const webcamRef = useRef(null);
  
  // Camera facing mode state - determines which camera is used
  // 'user' = front-facing camera (selfie mode)
  // 'environment' = back-facing camera (main camera)
  // Default to back camera for better quality in most scenarios
  const [facingMode, setFacingMode] = useState("environment");
  
  // Flash mode state - controls camera flashlight/torch functionality
  // 'off' = flashlight disabled (default for battery saving)
  // 'on' = flashlight enabled (for low-light situations)
  // 'auto' = automatic flash based on lighting conditions (if supported)
  const [flashMode, setFlashMode] = useState("off");
  
  // Aspect ratio state - determines camera preview and capture dimensions
  // '1:1' = Square format (Instagram-style)
  // '4:3' = Standard photo format
  // '9:16' = Portrait format (mobile-oriented)
  // '16:9' = Landscape format (widescreen)
  // Default to portrait for mobile-first experience
  const [ratio, setRatio] = useState("9:16");
  
  // Camera error state - stores and displays camera-related error messages
  // null = no error, string = error message to display to user
  const [cameraError, setCameraError] = useState(null);
  
  // Timer state for delayed photo capture functionality
  // 0 = immediate capture (no delay)
  // 3 = 3-second delay countdown
  // 10 = 10-second delay countdown
  const [timer, setTimer] = useState(0); // 0 | 3 | 10
  
  // Countdown state - displays remaining seconds during timer countdown
  // null = no countdown active
  // number = seconds remaining (3, 2, 1)
  const [countdown, setCountdown] = useState(null);

  /**
   * Toggles between front and back camera
   * 
   * This function switches the camera facing mode between 'user' (front)
   * and 'environment' (back). It uses a simple toggle pattern that inverts
   * the current mode, allowing users to switch between selfie and main camera.
   * 
   * Camera Switching Behavior:
   * - Front camera → Back camera
   * - Back camera → Front camera
   * - Triggers webcam re-initialization with new facing mode
   * - Maintains all other camera settings (flash, ratio, etc.)
   * 
   * @returns {void} - Updates facingMode state
   */
  const flipCamera = () => {
    setFacingMode((prev) => (prev === "user" ? "environment" : "user"));
  };

  /**
   * Handles camera-related errors with user-friendly messages
   * 
   * This function processes different types of camera errors and provides
   * appropriate user-friendly messages. It handles common scenarios like
   * permission denial, device not found, and generic camera failures.
   * 
   * Error Types Handled:
   * - NotAllowedError: User denied camera permission
   * - NotFoundError: No camera device available
   * - Generic errors: Fallback for other camera issues
   * 
   * @param {Error|null} error - Camera error object from getUserMedia or null to clear error
   * @returns {void} - Updates cameraError state with appropriate message
   */
  const handleCameraError = (error) => {
    if (error.name === "NotAllowedError") {
      // User explicitly denied camera permission through browser prompt
      // Provide clear guidance and alternative option (gallery upload)
      setCameraError(
        "Camera permission denied. Please allow camera access to use this feature or Choose from gallery",
      );
    } else if (error.name === "NotFoundError") {
      // No camera hardware detected or all cameras are in use
      // Suggest hardware troubleshooting steps
      setCameraError("No camera found. Please connect a camera and try again.");
    } else if (error) {
      // Catch-all for other camera-related errors
      // Include technical details for debugging while keeping user-friendly
      setCameraError("Camera error: " + error.message);
    } else {
      // Clear error state when no error is present
      // Used for error recovery and state reset
      setCameraError(null);
    }
  };

  // Return hook state and utility functions for component consumption
  // Components can destructure this object to access all camera functionality
  return {
    webcamRef, // DOM reference for webcam element - required for screenshot capture
    flipCamera, // Camera switching function - toggles between front/back cameras
    facingMode, // Current camera direction - 'user' (front) or 'environment' (back)
    flashMode, // Current flash setting - 'off', 'on', or 'auto'
    setFlashMode, // Flash mode updater - changes flashlight behavior
    ratio, // Current aspect ratio - determines preview/capture dimensions
    setRatio, // Aspect ratio updater - changes camera framing
    cameraError, // Camera error message - null if no error, string if error exists
    handleCameraError, // Error handler - processes camera errors with user messages
    timer, // Timer duration - 0 (immediate), 3, or 10 seconds for delayed capture
    setTimer, // Timer setter - configures countdown duration
    countdown, // Countdown value - null when inactive, number when counting down
    setCountdown // Countdown setter - updates display during timer countdown
  };
};

export default useWebcamHook;
